import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import { useDispatch, useSelector } from 'react-redux'
import { Route, Switch, useLocation, matchPath } from 'react-router-dom'

// components
import AppLayout from 'src/App/Layout/AppLayout'
import FavoriteListings from 'src/Components/homeSearch/FavoriteListings'
import { HomeSearchFooter } from 'src/Components/homeSearch/HomeSearchFooter'
import { ListingDetails } from 'src/Components/homeSearch/ListingDetails'
import Listings from 'src/Components/homeSearch/Listings'
import Loading from 'src/Components/shared/Loading/LoadingApp'
import Modal from 'src/Components/shared/Modal'
import NotFound from 'src/routes/NotFound'
import SavedSearches from 'src/Components/homeSearch/SavedSearches'
import { Search } from 'src/Components/homeSearch/Search'
import SignUp from 'src/features/SignUp/SignUp'
import { SearchModal } from 'src/Components/homeSearch/Search/SearchModal'

// actions
import { setCurrentClient, fetchClientData } from 'src/actions/client'
import { fetchProfile } from 'src/actions/customerProfile'
import { toggleAuthModal } from 'src/actions/homeSearchAuthModal'
import { toggleListingSearchModal } from 'src/actions/listings'

// selectors
import { selectHomebotListingsFeature } from 'src/store/selectors/customerProfile'
import { selectHomeSearchAuthModalVisible } from 'src/store/selectors/homeSearchAuthModal'
import {
  selectListingsSearchIsLoading,
  selectShowListingSearchModal,
} from 'src/store/selectors/listings'

// helpers
import { getClientIdFromBearerToken } from 'src/auth'
import { endsWith } from 'lodash'
import { getCurrentPath } from 'src/helpers/browser'
import { PATH_HELPERS } from 'src/constants/navigation'

// styles
import { ActiveOverlayContextProvider } from 'src/Components/shared/Routing/OverlayContext'
import styles from './homeSearch.module.scss'

const SearchRoutes = ({ match }) => {
  const { customerProfileId: customerProfileIdFromUrl } = match.params
  const dispatch = useDispatch()
  const location = useLocation()
  const [matchOverride, setMatchOverride] = useState({ isExact: true })
  const showAuthModal = useSelector(selectHomeSearchAuthModalVisible)
  const showSearchModal = useSelector(selectShowListingSearchModal)
  const loading = useSelector(selectListingsSearchIsLoading)
  const clientIdFromToken = getClientIdFromBearerToken()
  const isListingsPath = !!matchPath(getCurrentPath(), {
    path: PATH_HELPERS.listings.matchPath,
  })
  const isListingPath = !!matchPath(getCurrentPath(), {
    path: PATH_HELPERS.listing.matchPath,
  })
  const searchEnabled = useSelector(selectHomebotListingsFeature)

  // Hack to override match.isExact required by AppLayout
  // that is handed to OverlayMask to hide background and
  // header when the preferences panel is shown.
  // This is needed because we have one AppLayout that wraps
  // HomeSearch route based components.
  useEffect(() => {
    if (
      endsWith(location.pathname, 'listings/preferences') ||
      endsWith(location.pathname, 'photos') ||
      endsWith(location.pathname, 'map')
    ) {
      setMatchOverride({ isExact: false })
    } else {
      setMatchOverride({ isExact: true })
    }
  }, [location.pathname, setMatchOverride])

  useEffect(() => {
    if (customerProfileIdFromUrl) {
      dispatch(fetchProfile(customerProfileIdFromUrl))
    }
  }, [dispatch, customerProfileIdFromUrl])

  useEffect(() => {
    if (clientIdFromToken) {
      dispatch(setCurrentClient(clientIdFromToken))
    }
    dispatch(fetchClientData(clientIdFromToken, customerProfileIdFromUrl))
  }, [dispatch, clientIdFromToken, customerProfileIdFromUrl])

  if (loading) {
    return <Loading />
  }

  if (!searchEnabled) {
    return <NotFound resetState={false} />
  }

  return (
    <AppLayout enableAgentDropdown loading={false} match={matchOverride}>
      <Modal
        className={classnames(styles.modal, styles.authModal)}
        open={showAuthModal}
        toggle={() => dispatch(toggleAuthModal(false))}
        overrideContent
      >
        <SignUp
          className={styles.signUp}
          handleClose={() => dispatch(toggleAuthModal(false))}
        />
      </Modal>
      <Modal
        className={classnames(styles.modal, styles.searchModal)}
        open={showSearchModal}
        toggle={() => dispatch(toggleListingSearchModal(false))}
        overrideContent
      >
        <SearchModal />
      </Modal>

      <ActiveOverlayContextProvider>
        <Switch>
          <Route path={`${match.url}/listings`} component={Listings} />
          <Route
            path={`${match.url}/listing/:listingId`}
            component={ListingDetails}
          />
          <Route
            path={`${match.url}/saved-searches`}
            component={SavedSearches}
          />
          <Route
            path={`${match.url}/favorite-listings`}
            component={FavoriteListings}
          />
          <Route path={`${match.url}/`} component={Search} />
          <Route path='*' component={NotFound} />
        </Switch>
      </ActiveOverlayContextProvider>
      {!(isListingsPath || isListingPath) && <HomeSearchFooter />}
    </AppLayout>
  )
}

SearchRoutes.propTypes = {
  match: PropTypes.shape({
    isExact: PropTypes.bool,
    params: PropTypes.object,
    path: PropTypes.string,
    url: PropTypes.string,
  }),
}

export default SearchRoutes
